import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import {
  clearPersistedPlatformUserSelection,
  clearPlatformUserDrafts,
  hasAnyPlatformUserDrafts,
  loadPlatformUserDrafts,
  summarizeDraftsForDialog,
} from "userManagement/platformDraftsStorage";

import {
  Box,
  Button,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  useTheme,
} from "@mui/material";

import {
  LoginSharp,
  LogoutSharp,
  Menu as MenuIcon,
  Settings,
} from "@mui/icons-material";

import {
  CHCM_LOGO_MARK_WHITE_URL,
  TOP_BAR_LOGO_HEIGHT_PX,
  SHELL_TOP_BAR_PX,
} from "config";
import { useLogoutMutation } from "features/auth/auth";
import { getIntegrationsSettingsUnsaved } from "features/integrationsBeta/integrationsSettingsUnsaved";
import { getSftpNavigationUnsaved } from "features/sftp/sftpNavigationUnsaved";
import { useAppDispatch, useAppSelector } from "features/store";
import { catalogOffers } from "utils/navModel";
import ShellSidebar, {
  ShellNavButtonRow,
  ShellSidebarColumn,
} from "components/shell/ShellSidebar";
import TopBarAccount, { welcomeName } from "components/shell/TopBarAccount";
import { CALIBRATE_COLORS } from "components/hoc/Theme";
import useNavCatalog from "hooks/useNavCatalog";
import useResponsive from "hooks/useResponsive";
import { pushNotification } from "features/notificationSlice";
import {
  secureUploadsApi,
  useGetSecureUnreadCountQuery,
} from "features/secureUploads/secureUploads";

type HeaderProps = {
  children?: React.ReactNode;
};

const Header = ({ children }: HeaderProps) => {
  const theme = useTheme();
  const { isCardMode: isMobile, isPhone } = useResponsive();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [logout] = useLogoutMutation();

  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [unsavedDraftsOpen, setUnsavedDraftsOpen] = useState(false);
  const [unsavedDraftRows, setUnsavedDraftRows] = useState<
    ReturnType<typeof summarizeDraftsForDialog>
  >([]);
  const [pendingAfterDiscard, setPendingAfterDiscard] = useState<
    null | { kind: "navigate"; path: string } | { kind: "logout" }
  >(null);
  const [sftpUnsavedOpen, setSftpUnsavedOpen] = useState(false);
  const [pendingSftpAfterDiscard, setPendingSftpAfterDiscard] = useState<
    null | { kind: "navigate"; path: string } | { kind: "logout" }
  >(null);
  const [integrationsUnsavedOpen, setIntegrationsUnsavedOpen] = useState(false);
  const [pendingIntegrationsAfterDiscard, setPendingIntegrationsAfterDiscard] =
    useState<null | { kind: "navigate"; path: string } | { kind: "logout" }>(
      null,
    );

  const pp = user?.platform_profile;
  const { catalog } = useNavCatalog();
  const showSecureFileUpload = catalogOffers(catalog, "secureExchange");

  // A window grown past the drawer's width keeps the column instead.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const [isTabVisible, setIsTabVisible] = useState(
    typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  useEffect(() => {
    const onVisibility = () =>
      setIsTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const { data: secureUnread, refetch: refetchSecureUnread } =
    useGetSecureUnreadCountQuery(undefined, {
      skip: !isAuthenticated || !showSecureFileUpload,
      pollingInterval: isTabVisible ? 30000 : 0,
    });

  useEffect(() => {
    if (isTabVisible && isAuthenticated && showSecureFileUpload) {
      refetchSecureUnread();
    }
  }, [
    isTabVisible,
    isAuthenticated,
    showSecureFileUpload,
    refetchSecureUnread,
  ]);
  const secureUnreadCount = secureUnread?.unread ?? 0;

  useEffect(() => {
    if (!isAuthenticated || !showSecureFileUpload || secureUnread == null)
      return;
    const SEEN_KEY = `secureUnreadSeen:${user?.id ?? "anon"}`;
    const count = secureUnread.unread;
    let prev: number | null = null;
    try {
      const raw = sessionStorage.getItem(SEEN_KEY);
      prev = raw == null ? null : parseInt(raw, 10);
    } catch {
      prev = null;
    }

    if (prev == null) {
      if (count > 0) {
        dispatch(
          pushNotification({
            message: `You have ${count} new file${count > 1 ? "s" : ""} in Secure Exchange.`,
            severity: "info",
            autoHideMs: 10000,
          }),
        );
      }
    } else if (count > prev) {
      const delta = count - prev;
      dispatch(
        pushNotification({
          message: `You received ${delta} new file${delta > 1 ? "s" : ""} in Secure Exchange.`,
          severity: "info",
          autoHideMs: 10000,
        }),
      );
      // The page in front shows them too, not only the badge.
      dispatch(secureUploadsApi.util.invalidateTags(["SecureUploads"]));
    }

    try {
      sessionStorage.setItem(SEEN_KEY, String(count));
    } catch {}
  }, [secureUnread, isAuthenticated, showSecureFileUpload, user?.id, dispatch]);

  const isResetPasswordRoute = location.pathname === "/reset-password";
  const showAuthenticatedChrome =
    isAuthenticated && user !== null && !isResetPasswordRoute;

  const handleMenuClose = () => setAnchorEl(null);

  const performLogout = () => {
    logout()
      .unwrap()
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout failed:", error));
  };

  const openUnsavedDraftsDialog = (
    next: { kind: "navigate"; path: string } | { kind: "logout" },
  ) => {
    setUnsavedDraftRows(summarizeDraftsForDialog(loadPlatformUserDrafts()));
    setPendingAfterDiscard(next);
    setUnsavedDraftsOpen(true);
  };

  const openSftpUnsavedDialog = (
    next: { kind: "navigate"; path: string } | { kind: "logout" },
  ) => {
    setPendingSftpAfterDiscard(next);
    setSftpUnsavedOpen(true);
  };

  const openIntegrationsUnsavedDialog = (
    next: { kind: "navigate"; path: string } | { kind: "logout" },
  ) => {
    setPendingIntegrationsAfterDiscard(next);
    setIntegrationsUnsavedOpen(true);
  };

  const requestNavigate = (path: string) => {
    const onUserManagement = location.pathname === "/user-management";
    const leavingUserManagement =
      onUserManagement && path !== "/user-management";
    if (
      leavingUserManagement &&
      hasAnyPlatformUserDrafts(loadPlatformUserDrafts())
    ) {
      openUnsavedDraftsDialog({ kind: "navigate", path });
      return;
    }

    const onSftp = location.pathname.startsWith("/sftp-management");
    const leavingSftp = onSftp && !path.startsWith("/sftp-management");
    if (leavingSftp && getSftpNavigationUnsaved()) {
      openSftpUnsavedDialog({ kind: "navigate", path });
      return;
    }

    if (getIntegrationsSettingsUnsaved()) {
      openIntegrationsUnsavedDialog({ kind: "navigate", path });
      return;
    }

    // As a link does: the page already open is replaced, not stacked.
    navigate(path, { replace: path === location.pathname });
  };

  const onLogout = () => {
    handleMenuClose();
    if (hasAnyPlatformUserDrafts(loadPlatformUserDrafts())) {
      openUnsavedDraftsDialog({ kind: "logout" });
      return;
    }
    if (getSftpNavigationUnsaved()) {
      openSftpUnsavedDialog({ kind: "logout" });
      return;
    }
    if (getIntegrationsSettingsUnsaved()) {
      openIntegrationsUnsavedDialog({ kind: "logout" });
      return;
    }
    performLogout();
  };

  const handleAccountSettings = () => {
    handleMenuClose();
    requestNavigate("/account-settings");
  };

  const closeDrawerAndNavigate = (path: string) => {
    setDrawerOpen(false);
    requestNavigate(path);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      location.pathname === "/user-management" &&
      hasAnyPlatformUserDrafts(loadPlatformUserDrafts())
    ) {
      e.preventDefault();
      requestNavigate("/");
      return;
    }
    if (
      location.pathname.startsWith("/sftp-management") &&
      getSftpNavigationUnsaved()
    ) {
      e.preventDefault();
      requestNavigate("/");
      return;
    }
    if (getIntegrationsSettingsUnsaved()) {
      e.preventDefault();
      requestNavigate("/");
    }
  };

  const clientProfileName = welcomeName(user?.first_name, user?.username);

  const botsBackLabel = "Back to Automations";
  const goBackToBots = () => {
    const target = window.sessionStorage.getItem("botsReturnTo");
    window.sessionStorage.removeItem("botsReturnTo");
    if (target) navigate(target);
  };

  const userMenuPaperSx = {
    mt: 1.25,
    minWidth: 220,
    borderRadius: 1,
    border: "1px solid rgba(0,0,0,0.2)",
    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
  };

  const shellHeaderMenuSlotProps = {
    root: {
      sx: { zIndex: theme.zIndex.tooltip },
    },
  };

  const userDropdownMenu = (
    <Menu
      id="user-menu"
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={shellHeaderMenuSlotProps}
      PaperProps={{ sx: userMenuPaperSx }}
      MenuListProps={{ dense: true, sx: { py: 0.5 } }}
    >
      <MenuItem
        onClick={handleAccountSettings}
        sx={{
          py: 1,
          "&:hover": { bgcolor: "rgba(49, 129, 159, 0.1)" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Settings sx={{ fontSize: 20, color: "text.primary" }} />
        </ListItemIcon>
        <ListItemText
          primary="Account Settings"
          primaryTypographyProps={{
            fontSize: 14,
            fontFamily: theme.typography.fontFamily,
            color: "text.primary",
          }}
        />
      </MenuItem>
      <MenuItem
        onClick={onLogout}
        sx={{
          py: 1,
          "&:hover": { bgcolor: CALIBRATE_COLORS.dangerHover },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <LogoutSharp sx={{ fontSize: 20, color: "error.main" }} />
        </ListItemIcon>
        <ListItemText
          primary="Logout"
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: 600,
            fontFamily: theme.typography.fontFamily,
            color: "error.main",
          }}
        />
      </MenuItem>
    </Menu>
  );

  const topBarRight = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        // Shrinks before anything else: the greeting gives way first.
        flexShrink: 1,
        minWidth: 0,
        pr: "10px",
        minHeight: SHELL_TOP_BAR_PX,
        maxHeight: SHELL_TOP_BAR_PX,
      }}
    >
      {showAuthenticatedChrome ? (
        <>
          <TopBarAccount
            name={clientProfileName}
            menuId="user-menu"
            menuOpen={menuOpen}
            onOpenMenu={setAnchorEl}
          />
          {userDropdownMenu}
        </>
      ) : (
        location.pathname !== "/login" &&
        !isResetPasswordRoute &&
        location.pathname !== "/integrations" && (
          <Button
            startIcon={<LoginSharp />}
            href="/login"
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: "rgba(255,255,255,0.15)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.4)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            Login
          </Button>
        )
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="header"
          sx={{
            width: "100%",
            height: SHELL_TOP_BAR_PX,
            minHeight: SHELL_TOP_BAR_PX,
            maxHeight: SHELL_TOP_BAR_PX,
            flexShrink: 0,
            boxSizing: "border-box",
            bgcolor: "primary.main",
            boxShadow: theme.palette.calibrate.topBarShadow,
            display: "flex",
            alignItems: "center",
            zIndex: theme.zIndex.appBar,
            overflow: "visible",
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: SHELL_TOP_BAR_PX,
              maxHeight: SHELL_TOP_BAR_PX,
              height: SHELL_TOP_BAR_PX,
              width: "100%",
              px: 0,
              py: 0,
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            {isMobile && showAuthenticatedChrome && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: "#fff", ml: 0.5, flexShrink: 0 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link
              to="/"
              onClick={handleLogoClick}
              style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <Box
                component="img"
                src={CHCM_LOGO_MARK_WHITE_URL}
                alt="Calibrate HCM"
                sx={{
                  height: TOP_BAR_LOGO_HEIGHT_PX,
                  minHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  maxHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  width: "auto",
                  ml: "10px",
                  cursor: "pointer",
                  display: "block",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </Link>
            {location.pathname.startsWith("/data-storage") &&
              window.sessionStorage.getItem("botsReturnTo") != null &&
              (isPhone ? (
                <Tooltip title={botsBackLabel}>
                  <IconButton
                    size="small"
                    aria-label={botsBackLabel}
                    onClick={goBackToBots}
                    sx={{ color: "#fff", ml: 0.5, flexShrink: 0 }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  onClick={goBackToBots}
                  sx={{
                    ml: 2,
                    flexShrink: 0,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.5)",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  {botsBackLabel}
                </Button>
              ))}
            <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            {topBarRight}
          </Toolbar>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {showAuthenticatedChrome && !isMobile && (
            <ShellSidebarColumn>
              <ShellSidebar
                catalog={catalog}
                pathname={location.pathname}
                onNavigate={requestNavigate}
                unreadCount={secureUnreadCount}
                ariaLabel="Main navigation"
                scrollKey="main"
              />
            </ShellSidebarColumn>
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              bgcolor: "background.default",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            bgcolor: "primary.main",
            height: "100%",
            minHeight: "100%",
            backgroundImage: "none",
            overflow: "hidden",
          },
        }}
      >
        <ShellSidebar
          drawer
          catalog={catalog}
          pathname={location.pathname}
          onNavigate={closeDrawerAndNavigate}
          unreadCount={secureUnreadCount}
          ariaLabel="Main navigation"
          scrollKey="main-drawer"
          footer={
            <>
              <ShellNavButtonRow
                icon={Settings}
                label="Account settings"
                onClick={() => {
                  setDrawerOpen(false);
                  handleAccountSettings();
                }}
              />
              <ShellNavButtonRow
                icon={LogoutSharp}
                label="Logout"
                danger
                onClick={() => {
                  setDrawerOpen(false);
                  onLogout();
                }}
              />
            </>
          }
        />
      </Drawer>

      <UnsavedPlatformDraftsDialog
        open={unsavedDraftsOpen}
        onClose={() => {
          setUnsavedDraftsOpen(false);
          setPendingAfterDiscard(null);
        }}
        rows={unsavedDraftRows}
        onDiscardAndContinue={() => {
          clearPlatformUserDrafts();
          clearPersistedPlatformUserSelection();
          setUnsavedDraftsOpen(false);
          const pending = pendingAfterDiscard;
          setPendingAfterDiscard(null);
          if (!pending) return;
          if (pending.kind === "navigate") {
            navigate(pending.path);
          } else {
            performLogout();
          }
        }}
      />

      <UnsavedPlatformDraftsDialog
        open={sftpUnsavedOpen}
        onClose={() => {
          setSftpUnsavedOpen(false);
          setPendingSftpAfterDiscard(null);
        }}
        rows={[]}
        title="Unsaved SFTP changes"
        intro="You have unsaved changes in SFTP Setup. Stay to keep editing, or discard them and continue. Unsaved edits will be lost."
        showWarning={true}
        onDiscardAndContinue={() => {
          setSftpUnsavedOpen(false);
          const pending = pendingSftpAfterDiscard;
          setPendingSftpAfterDiscard(null);
          if (!pending) return;
          if (pending.kind === "navigate") {
            navigate(pending.path);
          } else {
            performLogout();
          }
        }}
      />

      <UnsavedPlatformDraftsDialog
        open={integrationsUnsavedOpen}
        onClose={() => {
          setIntegrationsUnsavedOpen(false);
          setPendingIntegrationsAfterDiscard(null);
        }}
        rows={[]}
        title="Unsaved integration settings"
        intro="You have unsaved changes in the integration's Settings. Stay to keep editing, or discard them and continue. Unsaved edits will be lost."
        showWarning={true}
        onDiscardAndContinue={() => {
          setIntegrationsUnsavedOpen(false);
          const pending = pendingIntegrationsAfterDiscard;
          setPendingIntegrationsAfterDiscard(null);
          if (!pending) return;
          if (pending.kind === "navigate") {
            navigate(pending.path);
          } else {
            performLogout();
          }
        }}
      />
    </>
  );
};

export default Header;
